import { useDialog } from 'naive-ui'

/**
 * Composable for showing confirmation dialogs using naive-ui's dialog provider.
 * Replaces native browser confirm() with styled in-app modals.
 * Uses dialog.create() for a clean look aligned with Intercom design language.
 *
 * Usage:
 *   const { confirm } = useConfirm()
 *   const ok = await confirm('Delete this item?', { title: 'Confirm Delete', type: 'error' })
 *   if (ok) { ... }
 */
export function useConfirm() {
  const dialog = useDialog()

  /**
   * Show a confirmation dialog and return a promise that resolves to true/false.
   * @param {string} content - The message to display
   * @param {Object} options - Optional overrides
   * @param {string} options.title - Dialog title (default: 'Confirm')
   * @param {string} options.type - 'error' for destructive actions (red button), default is primary blue
   * @param {string} options.positiveText - Confirm button text (default: 'Confirm')
   * @param {string} options.negativeText - Cancel button text (default: 'Cancel')
   * @returns {Promise<boolean>}
   */
  const confirm = (content, options = {}) => {
    const isDestructive = options.type === 'error'

    return new Promise((resolve) => {
      dialog.create({
        title: options.title || 'Confirm',
        content,
        positiveText: options.positiveText || 'Confirm',
        negativeText: options.negativeText || 'Cancel',
        positiveButtonProps: {
          type: isDestructive ? 'error' : 'primary',
        },
        onPositiveClick: () => resolve(true),
        onNegativeClick: () => resolve(false),
        onClose: () => resolve(false),
        onMaskClick: () => resolve(false),
      })
    })
  }

  return { confirm }
}
